import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { readFragment } from 'gql.tada';
import {
  CalendarClock,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Copy,
  MapPin,
  Pencil,
  Plus,
  Printer,
  Trash2,
  Users,
} from 'lucide-react';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import type { AppLayoutContext } from '~/types';
import ScheduleBlockDialog, {
  termOption,
  type ClassOption,
  type ScheduleBlockValue,
  type StaffOption,
  type StudentOption,
} from './ScheduleBlockDialog';
import ScheduleTimeGrid from './ScheduleTimeGrid';
import { WEEKDAY_LABELS, type Weekday } from './scheduleUtils';
import {
  ScheduleBlockFields,
  useCopyYearSchedule,
  useDeleteScheduleBlock,
  useScheduleWorkspaceQuery,
  useUpsertScheduleBlockOccurrence,
} from './useScheduleBlocks';

const ALL = '__all__';
const NONE = '__none__';
const TERMINAL_STUDENT_ENROLLMENT_STATES = new Set(['WITHDRAWN', 'DENIED', 'NOT_RETURNING']);

type PeriodMode = 'DAY' | 'WEEK';
type PrintMode = 'SCHOOL' | 'STUDENT' | 'TEACHER' | 'ALL_STUDENTS';

interface PrintPayload {
  mode: PrintMode;
  schoolName: string;
  startsOn: string;
  endsOn: string;
  entries: ReadonlyArray<SchedulePeriodEntry>;
  studentPersonId?: string;
  studentName?: string;
  teacherPersonId?: string;
  teacherName?: string;
  students: ReadonlyArray<{ id: string; name: string }>;
}

interface SchedulePeriodEntry {
  date: string;
  startsAt: string;
  endsAt: string;
  timeLabel: string;
  entryType: string;
  title: string;
  classroom?: { id: string; name: string } | null;
  classOffering?: { id: string; displayName: string; term: { id: string; name: string } } | null;
  scheduleBlock: { id: string; attendanceEnabled: boolean };
  studentPeople: ReadonlyArray<{ id: string }>;
  staffPeople: ReadonlyArray<{ id: string }>;
}

interface MomentContext {
  schoolYearId: string;
  date: string;
  time: string;
  staffPersonId: string;
  studentPersonId: string;
  classroomId: string;
}

interface MomentMessage extends MomentContext {
  blockId: string;
  text: string;
}

function localDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function localTimeString(date = new Date()) {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function parseLocalDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function shiftDate(value: string, days: number) {
  const date = parseLocalDate(value);
  date.setDate(date.getDate() + days);
  return localDateString(date);
}

function clampDate(value: string, startsOn: string, endsOn: string) {
  if (!startsOn || !endsOn) return value;
  if (value < startsOn) return startsOn;
  if (value > endsOn) return endsOn;
  return value;
}

function initialDateForYear(value: string, startsOn: string, endsOn: string) {
  if (!startsOn || !endsOn) return value;
  return value < startsOn || value > endsOn ? startsOn : value;
}

function mondayOf(value: string) {
  const date = parseLocalDate(value);
  const day = date.getDay();
  date.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
  return localDateString(date);
}

function schedulePeriodBounds(
  value: string,
  mode: PeriodMode,
  schoolYearStart: string,
  schoolYearEnd: string,
) {
  const selectedDate = clampDate(value, schoolYearStart, schoolYearEnd);
  if (mode === 'DAY') return { periodStart: selectedDate, periodEnd: selectedDate };

  const weekStart = mondayOf(selectedDate);
  const weekEnd = shiftDate(weekStart, 4);
  const periodStart = schoolYearStart && weekStart < schoolYearStart ? schoolYearStart : weekStart;
  const periodEnd = schoolYearEnd && weekEnd > schoolYearEnd ? schoolYearEnd : weekEnd;

  // A year can begin or end on a weekend, leaving no Mon-Fri intersection.
  // Keep the request valid and show that boundary day as a one-day agenda.
  return periodStart <= periodEnd
    ? { periodStart, periodEnd }
    : { periodStart: selectedDate, periodEnd: selectedDate };
}

function actionFailure(prefix: string, error: unknown) {
  return error instanceof Error && error.message ? `${prefix}: ${error.message}` : prefix;
}

function refreshFailure(savedAction: string, error: unknown) {
  const detail = error instanceof Error && error.message ? `: ${error.message}` : '';
  return `${savedAction}, but the schedule could not be refreshed${detail}. Reload the page to see the saved changes.`;
}

function sameMomentContext(first: MomentContext | null, second: MomentContext) {
  return first?.schoolYearId === second.schoolYearId &&
    first.date === second.date &&
    first.time === second.time &&
    first.staffPersonId === second.staffPersonId &&
    first.studentPersonId === second.studentPersonId &&
    first.classroomId === second.classroomId;
}

function formatPeriodDate(value: string, includeYear = false) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' } : {}),
  }).format(parseLocalDate(value));
}

function personName(person: { firstName: string; lastName: string }) {
  return `${person.firstName} ${person.lastName}`.trim();
}

function compareText(first: string, second: string) {
  if (first < second) return -1;
  if (first > second) return 1;
  return 0;
}

function kindLabel(kind: string) {
  return kind.toLowerCase().replace(/_/g, ' ').replace(/^./, (character) => character.toUpperCase());
}

function snapshotPeriodEntries(entries: ReadonlyArray<SchedulePeriodEntry>): ReadonlyArray<SchedulePeriodEntry> {
  return entries.map((entry) => ({
    ...entry,
    classroom: entry.classroom ? { ...entry.classroom } : entry.classroom,
    classOffering: entry.classOffering
      ? { ...entry.classOffering, term: { ...entry.classOffering.term } }
      : entry.classOffering,
    scheduleBlock: { ...entry.scheduleBlock },
    studentPeople: entry.studentPeople.map((person) => ({ ...person })),
    staffPeople: entry.staffPeople.map((person) => ({ ...person })),
  }));
}

function blockPattern(block: ScheduleBlockValue) {
  if (block.oneTime) return block.oneTimeDate ? formatPeriodDate(block.oneTimeDate, true) : 'One-time event';
  return block.weekdays.map((day) => WEEKDAY_LABELS[day as Weekday] ?? day).join(', ');
}

export default function OperationalSchedule() {
  const { schoolSlug, yearSlug } = useParams();
  const navigate = useNavigate();
  const { school, roleContext } = useOutletContext<AppLayoutContext>();
  const configureTermsHref = schoolSlug && yearSlug
    ? `/app/${schoolSlug}/settings?tab=school-years&year=${yearSlug}#academic-terms`
    : undefined;
  const schoolYear = school.schoolYears?.find((year) => year.slug === yearSlug);
  const selectedYearStart = String(schoolYear?.startDate ?? '');
  const selectedYearEnd = String(schoolYear?.endDate ?? '');
  const isAdmin = roleContext.selectedRole === 'PRIMARY_ADMIN' || roleContext.selectedRole === 'ADMIN';
  const [date, setDate] = useState(() => initialDateForYear(localDateString(), selectedYearStart, selectedYearEnd));
  const [time, setTime] = useState(localTimeString);
  const [periodMode, setPeriodMode] = useState<PeriodMode>('WEEK');
  const [printPayload, setPrintPayload] = useState<PrintPayload | null>(null);
  const [staffPersonId, setStaffPersonId] = useState(ALL);
  const [studentPersonId, setStudentPersonId] = useState(ALL);
  const [classroomId, setClassroomId] = useState(ALL);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDate, setDialogDate] = useState(localDateString);
  const [dialogStartsAt, setDialogStartsAt] = useState('08:00');
  const [dialogEndsAt, setDialogEndsAt] = useState('09:00');
  const [editing, setEditing] = useState<ScheduleBlockValue | null>(null);
  const [deleting, setDeleting] = useState<ScheduleBlockValue | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [sourceYearId, setSourceYearId] = useState(NONE);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [momentMessage, setMomentMessage] = useState<MomentMessage | null>(null);
  const [deleteBlock] = useDeleteScheduleBlock();
  const [copyYear, { loading: copying }] = useCopyYearSchedule();
  const [upsertOccurrence] = useUpsertScheduleBlockOccurrence();
  const previousSchoolYearId = useRef(schoolYear?.id);
  const activeSchoolYearId = useRef(schoolYear?.id);
  const activeMomentContext = useRef<MomentContext | null>(null);
  const momentRequestSequence = useRef(0);
  const printTimer = useRef<number | null>(null);
  const afterPrintCleanup = useRef<(() => void) | null>(null);
  activeSchoolYearId.current = schoolYear?.id;
  const stateMatchesSchoolYear = previousSchoolYearId.current === schoolYear?.id;

  const clearPrintLifecycle = useCallback(() => {
    if (printTimer.current !== null) {
      window.clearTimeout(printTimer.current);
      printTimer.current = null;
    }
    if (afterPrintCleanup.current) {
      window.removeEventListener('afterprint', afterPrintCleanup.current);
      afterPrintCleanup.current = null;
    }
    document.body.classList.remove('printing-schedule');
  }, []);

  const filters = useMemo(() => ({
    staffPersonId: !stateMatchesSchoolYear || staffPersonId === ALL ? undefined : staffPersonId,
    studentPersonId: !stateMatchesSchoolYear || studentPersonId === ALL ? undefined : studentPersonId,
    classroomId: !stateMatchesSchoolYear || classroomId === ALL ? undefined : classroomId,
  }), [classroomId, staffPersonId, stateMatchesSchoolYear, studentPersonId]);
  const queryDate = stateMatchesSchoolYear
    ? clampDate(date, selectedYearStart, selectedYearEnd)
    : initialDateForYear(localDateString(), selectedYearStart, selectedYearEnd);
  const { periodStart, periodEnd } = schedulePeriodBounds(
    queryDate,
    periodMode,
    selectedYearStart,
    selectedYearEnd,
  );
  const momentContext: MomentContext = {
    schoolYearId: schoolYear?.id ?? '',
    date: queryDate,
    time,
    staffPersonId,
    studentPersonId,
    classroomId,
  };
  activeMomentContext.current = momentContext;

  const { data, loading, error, refetch } = useScheduleWorkspaceQuery(
    isAdmin ? schoolYear?.id : undefined,
    queryDate,
    time,
    periodStart,
    periodEnd,
    filters,
  );
  const workspace = data?.schoolYear;
  const schoolYearStart = selectedYearStart || String(workspace?.startDate ?? '');
  const schoolYearEnd = selectedYearEnd || String(workspace?.endDate ?? '');
  const classrooms = workspace?.school.classrooms ?? [];
  const staff = useMemo(
    () => [...(workspace?.school.activeStaff ?? []), ...(workspace?.school.inactiveStaff ?? [])] as ReadonlyArray<StaffOption>,
    [workspace?.school.activeStaff, workspace?.school.inactiveStaff],
  );
  const students = useMemo(
    () => {
      const enrollments = workspace?.studentEnrollments ?? [];
      const enrollmentByPersonId = new Map(enrollments.map((enrollment) => [enrollment.person.id, enrollment]));
      const byPersonId = new Map<string, StudentOption>();

      enrollments
        .filter((enrollment) => enrollment.state === 'ENROLLED' || enrollment.state === 'ACCEPTED_PENDING_DEPOSIT')
        .forEach((enrollment) => byPersonId.set(enrollment.person.id, {
          person: enrollment.person,
          gradeLevel: enrollment.gradeLevel,
        }));

      (workspace?.courses ?? []).forEach((course) => course.classOfferings.forEach((classOffering) => {
        classOffering.rosterEntries
          .filter((entry) => entry.state === 'ACTIVE')
          .forEach((entry) => {
            const enrollment = enrollmentByPersonId.get(entry.person.id);
            if (enrollment && TERMINAL_STUDENT_ENROLLMENT_STATES.has(enrollment.state)) return;

            byPersonId.set(entry.person.id, {
              person: entry.person,
              gradeLevel: enrollment?.gradeLevel ?? null,
            });
          });
      }));

      return [...byPersonId.values()]
        .sort((first, second) => personName(first.person).localeCompare(personName(second.person)) || first.person.id.localeCompare(second.person.id));
    },
    [workspace?.courses, workspace?.studentEnrollments],
  );
  const classes = useMemo(
    () => (workspace?.courses ?? []).flatMap((course) => course.classOfferings.map((classOffering) => ({
      ...classOffering,
      courseId: course.id,
      courseName: course.name,
      section: classOffering.section,
      term: termOption(classOffering.term),
    }))) as ReadonlyArray<ClassOption>,
    [workspace?.courses],
  );
  const terms = useMemo(
    () => (workspace?.terms ?? []).map(termOption),
    [workspace?.terms],
  );
  const blocks = useMemo(
    () => (workspace?.scheduleBlocks ?? []).map((block) => readFragment(ScheduleBlockFields, block) as unknown as ScheduleBlockValue),
    [workspace?.scheduleBlocks],
  );
  const snapshotOccurrences = useMemo(
    () => (workspace?.scheduleSnapshot.occurrences ?? []).map((occurrence) => ({
      ...occurrence,
      block: readFragment(ScheduleBlockFields, occurrence.scheduleBlock) as unknown as ScheduleBlockValue,
    })),
    [workspace?.scheduleSnapshot.occurrences],
  );
  const visibleMomentMessage = momentMessage &&
    sameMomentContext(momentMessage, momentContext) &&
    snapshotOccurrences.some(({ block, ...occurrence }) =>
      block.id === momentMessage.blockId && String(occurrence.date) === momentMessage.date)
    ? momentMessage.text
    : null;
  const schedulePeriod = workspace?.schoolSchedulePeriod;
  const periodEntries = (schedulePeriod?.entries ?? []) as ReadonlyArray<SchedulePeriodEntry>;
  const periodReady = stateMatchesSchoolYear && workspace?.id === schoolYear?.id && !loading && !error &&
    schedulePeriod?.startsOn === periodStart && schedulePeriod?.endsOn === periodEnd;
  const gradeLevels = useMemo(
    () => [...(workspace?.school.gradeLevelsSetting?.items ?? [])]
      .sort((first, second) => first.order - second.order)
      .map((grade) => grade.description),
    [workspace?.school.gradeLevelsSetting?.items],
  );
  const printStudents = useMemo(() => {
    const configuredOrder = new Map(gradeLevels.map((gradeLevel, index) => [gradeLevel, index]));
    const unconfiguredPosition = gradeLevels.length;

    return [...students].sort((first, second) => {
      const firstPosition = configuredOrder.get(first.gradeLevel ?? '') ?? unconfiguredPosition;
      const secondPosition = configuredOrder.get(second.gradeLevel ?? '') ?? unconfiguredPosition;
      if (firstPosition !== secondPosition) return firstPosition - secondPosition;

      const gradeComparison = compareText(first.gradeLevel?.trim() || '\uffff', second.gradeLevel?.trim() || '\uffff');
      if (gradeComparison !== 0) return gradeComparison;

      const nameComparison = compareText(personName(first.person), personName(second.person));
      return nameComparison || compareText(first.person.id, second.person.id);
    });
  }, [gradeLevels, students]);
  const selectedStudentName = stateMatchesSchoolYear
    ? students.find((item) => item.person.id === studentPersonId)?.person
    : undefined;
  const selectedTeacherName = stateMatchesSchoolYear
    ? staff.find((item) => item.person.id === staffPersonId)?.person
    : undefined;
  const visibleEntries = (periodReady ? periodEntries : []).filter((entry) =>
    (studentPersonId === ALL || entry.studentPeople.some((person) => person.id === studentPersonId)) &&
    (staffPersonId === ALL || entry.staffPeople.some((person) => person.id === staffPersonId)) &&
    (classroomId === ALL || entry.classroom?.id === classroomId));
  const yearOptions = (school.schoolYears ?? []).filter((year) => year.id !== schoolYear?.id);

  useEffect(() => {
    setDate((current) => initialDateForYear(current, schoolYearStart, schoolYearEnd));
  }, [schoolYearEnd, schoolYearStart]);

  useEffect(() => {
    if (previousSchoolYearId.current === schoolYear?.id) return;

    previousSchoolYearId.current = schoolYear?.id;
    const nextDate = initialDateForYear(localDateString(), selectedYearStart, selectedYearEnd);
    setDate(nextDate);
    setStaffPersonId(ALL);
    setStudentPersonId(ALL);
    setClassroomId(ALL);
    setSourceYearId(NONE);
    setCopyMessage(null);
    setDialogOpen(false);
    setDialogDate(nextDate);
    setDialogStartsAt('08:00');
    setDialogEndsAt('09:00');
    setEditing(null);
    setDeleting(null);
    setDeleteError(null);
    setPrintPayload(null);
    clearPrintLifecycle();
  }, [clearPrintLifecycle, schoolYear?.id, selectedYearEnd, selectedYearStart]);

  useEffect(() => () => clearPrintLifecycle(), [clearPrintLifecycle]);

  useEffect(() => {
    clearPrintLifecycle();
    setPrintPayload(null);
  }, [clearPrintLifecycle, classroomId, periodEnd, periodMode, periodStart, staffPersonId, studentPersonId]);

  useEffect(() => {
    momentRequestSequence.current += 1;
    setMomentMessage(null);
  }, [classroomId, queryDate, schoolYear?.id, staffPersonId, studentPersonId, time]);

  useEffect(() => () => {
    momentRequestSequence.current += 1;
  }, []);

  if (!isAdmin) {
    return <div className="p-6"><Card><CardHeader><CardTitle>Schedule</CardTitle><CardDescription>Only school administrators can manage the school schedule.</CardDescription></CardHeader></Card></div>;
  }
  if (!schoolYear) {
    return <div className="p-6"><Card><CardHeader><CardTitle>School year not found</CardTitle></CardHeader></Card></div>;
  }

  const openCreate = (forDate = queryDate, startsAt = '08:00', endsAt = '09:00') => {
    setEditing(null);
    setDialogDate(forDate);
    setDialogStartsAt(startsAt);
    setDialogEndsAt(endsAt);
    setDialogOpen(true);
  };
  const openEdit = (block: ScheduleBlockValue) => {
    setEditing(block);
    setDialogDate(block.oneTimeDate ?? date);
    setDialogStartsAt(block.startsAt.slice(0, 5));
    setDialogEndsAt(block.endsAt.slice(0, 5));
    setDialogOpen(true);
  };
  const handleDelete = async () => {
    if (!deleting) return;
    const actionYearId = schoolYear.id;
    setDeleteError(null);
    setCopyMessage(null);
    let result;
    try {
      result = await deleteBlock({ variables: { input: { id: deleting.id } } });
    } catch (deleteFailure) {
      if (activeSchoolYearId.current === actionYearId) {
        setDeleteError(actionFailure('Unable to delete this schedule block', deleteFailure));
      }
      return;
    }
    if (activeSchoolYearId.current !== actionYearId) return;

    const errors = result.data?.deleteScheduleBlock?.userErrors ?? [];
    if (errors.length > 0) return setDeleteError(errors.map((item) => item.message).join(', '));
    setDeleting(null);
    try {
      await refetch();
    } catch (refreshError) {
      if (activeSchoolYearId.current === actionYearId) {
        setCopyMessage(refreshFailure('Schedule block deleted', refreshError));
      }
    }
  };
  const cancelOccurrence = async (block: ScheduleBlockValue, occursOn: string) => {
    const actionYearId = schoolYear.id;
    const actionContext = {
      ...momentContext,
      blockId: block.id,
      date: occursOn,
    };
    const requestSequence = ++momentRequestSequence.current;
    const showActionMessage = (text: string) => {
      if (momentRequestSequence.current !== requestSequence ||
          !sameMomentContext(activeMomentContext.current, actionContext)) return;

      setMomentMessage({ ...actionContext, text });
    };
    setMomentMessage(null);
    let result;
    try {
      result = await upsertOccurrence({ variables: { input: { scheduleBlockId: block.id, date: occursOn, state: 'CANCELLED' } } });
    } catch (cancelFailure) {
      showActionMessage(actionFailure('Unable to cancel this occurrence', cancelFailure));
      return;
    }
    if (activeSchoolYearId.current !== actionYearId) return;

    const errors = result.data?.upsertScheduleBlockOccurrence?.userErrors ?? [];
    if (errors.length > 0) {
      showActionMessage(errors.map((item) => item.message).join(', '));
      return;
    }
    try {
      await refetch();
    } catch (refreshError) {
      showActionMessage(refreshFailure('Occurrence cancelled', refreshError));
    }
  };
  const handleCopy = async () => {
    if (sourceYearId === NONE) return;
    const actionYearId = schoolYear.id;
    setCopyMessage(null);
    let result;
    try {
      result = await copyYear({ variables: { input: { sourceSchoolYearId: sourceYearId, targetSchoolYearId: schoolYear.id } } });
    } catch (copyFailure) {
      if (activeSchoolYearId.current === actionYearId) {
        setCopyMessage(actionFailure('Unable to copy the schedule', copyFailure));
      }
      return;
    }
    if (activeSchoolYearId.current !== actionYearId) return;

    const payload = result.data?.copyYearSchedule;
    const errors = payload?.userErrors ?? [];
    if (errors.length > 0) return setCopyMessage(errors.map((item) => item.message).join(', '));
    const copiedCount = payload?.copiedBlocks.length ?? 0;
    const warnings = payload?.unmatched ?? [];
    const warningSummary = warnings.length > 0
      ? ` with ${warnings.length} ${warnings.length === 1 ? 'warning' : 'warnings'}: ${warnings.map((warning) => `${warning.title}: ${warning.reason}`).join('; ')}`
      : '';
    const successMessage = `${copiedCount} ${copiedCount === 1 ? 'block' : 'blocks'} copied${warningSummary}.`;
    setCopyMessage(successMessage);
    try {
      await refetch();
    } catch (refreshError) {
      if (activeSchoolYearId.current === actionYearId) {
        setCopyMessage(`${successMessage} ${refreshFailure('The copy is saved', refreshError)}`);
      }
    }
  };
  const print = (mode: PrintMode) => {
    if (!periodReady) return;
    if (mode === 'STUDENT' && !selectedStudentName) return;
    if (mode === 'TEACHER' && !selectedTeacherName) return;

    clearPrintLifecycle();
    // Print from an immutable projection captured at the click. Apollo may
    // refresh the same query while the zero-delay print render is pending;
    // that must not swap the user-selected person, period, or entries.
    setPrintPayload({
      mode,
      schoolName: school.name,
      startsOn: periodStart,
      endsOn: periodEnd,
      entries: snapshotPeriodEntries(periodEntries),
      studentPersonId: mode === 'STUDENT' ? studentPersonId : undefined,
      studentName: mode === 'STUDENT' && selectedStudentName ? personName(selectedStudentName) : undefined,
      teacherPersonId: mode === 'TEACHER' ? staffPersonId : undefined,
      teacherName: mode === 'TEACHER' && selectedTeacherName ? personName(selectedTeacherName) : undefined,
      students: mode === 'ALL_STUDENTS'
        ? printStudents.map((student) => ({ id: student.person.id, name: personName(student.person) }))
        : [],
    });
    printTimer.current = window.setTimeout(() => {
      printTimer.current = null;
      const cleanup = () => {
        document.body.classList.remove('printing-schedule');
        window.removeEventListener('afterprint', cleanup);
        if (afterPrintCleanup.current === cleanup) afterPrintCleanup.current = null;
      };
      document.body.classList.add('printing-schedule');
      afterPrintCleanup.current = cleanup;
      window.addEventListener('afterprint', cleanup, { once: true });
      try {
        window.print();
      } catch (printError) {
        window.removeEventListener('afterprint', cleanup);
        cleanup();
        setCopyMessage(actionFailure('Unable to open the print dialog', printError));
      }
    }, 0);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="schedule-screen-controls flex flex-wrap items-start justify-between gap-4">
        <div><h1 className="text-3xl font-bold">Schedule</h1><p className="text-muted-foreground">One schedule for classes, chapel, recess, meetings, and school events.</p></div>
        <div className="flex gap-2">
          <Button asChild variant="outline"><Link to={`/app/${schoolSlug}/years/${yearSlug}/attendance`}><CalendarCheck className="mr-1 h-4 w-4" /> Attendance</Link></Button>
          <Button onClick={() => openCreate()}><Plus className="mr-1 h-4 w-4" /> Add block</Button>
        </div>
      </div>

      <Card className="schedule-screen-controls">
        <CardHeader><CardTitle className="flex items-center gap-2"><Copy className="h-5 w-5" /> Copy a prior year</CardTitle><CardDescription>Recurring blocks and dynamic audiences are copied. One-time events and dated overrides are reported as unmatched.</CardDescription></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="min-w-64 space-y-2"><Label>Source year</Label><Select value={sourceYearId} onValueChange={setSourceYearId} disabled={copying}><SelectTrigger aria-label="Source year"><SelectValue /></SelectTrigger><SelectContent><SelectItem value={NONE}>Choose a school year</SelectItem>{yearOptions.map((year) => <SelectItem key={year.id} value={year.id}>{year.name}</SelectItem>)}</SelectContent></Select></div>
          <Button variant="outline" disabled={copying || sourceYearId === NONE} onClick={handleCopy}>{copying ? 'Copying…' : 'Copy schedule'}</Button>
          {copyMessage && <p className="text-sm text-muted-foreground">{copyMessage}</p>}
        </CardContent>
      </Card>

      <Card className="schedule-screen-controls">
        <CardHeader className="schedule-screen-controls">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><CardTitle>Day and week agenda</CardTitle><CardDescription>Use a day heading to add a one-time or recurring block, then print the school, teacher, or student projection.</CardDescription></div>
            <div className="schedule-screen-controls flex flex-wrap gap-2">
              <Button size="sm" variant={periodMode === 'DAY' ? 'default' : 'outline'} onClick={() => setPeriodMode('DAY')}>Day</Button>
              <Button size="sm" variant={periodMode === 'WEEK' ? 'default' : 'outline'} onClick={() => setPeriodMode('WEEK')}>Week</Button>
              <Button size="sm" variant="outline" aria-label="Print school" disabled={!periodReady} onClick={() => print('SCHOOL')}><Printer className="mr-1 h-4 w-4" /> School</Button>
              <Button size="sm" variant="outline" aria-label="Print selected student" disabled={!periodReady || !selectedStudentName} onClick={() => print('STUDENT')}>Selected student</Button>
              <Button size="sm" variant="outline" aria-label="Print selected teacher" disabled={!periodReady || !selectedTeacherName} onClick={() => print('TEACHER')}>Selected teacher</Button>
              <Button size="sm" variant="outline" aria-label="Print all students" disabled={!periodReady} onClick={() => print('ALL_STUDENTS')}>All students</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="schedule-screen-controls grid gap-4 md:grid-cols-[auto_1fr_auto] md:items-end">
            <Button variant="outline" size="icon" aria-label="Previous schedule period" onClick={() => setDate(clampDate(shiftDate(date, periodMode === 'DAY' ? -1 : -7), schoolYearStart, schoolYearEnd))}><ChevronLeft className="h-4 w-4" /></Button>
            <div className="grid gap-4 lg:grid-cols-4">
              <div className="space-y-2"><Label htmlFor="schedule-period-date">{periodMode === 'DAY' ? 'Day' : 'Week containing'}</Label><Input id="schedule-period-date" type="date" min={schoolYearStart} max={schoolYearEnd} value={date} onChange={(event) => setDate(clampDate(event.target.value, schoolYearStart, schoolYearEnd))} /></div>
              <FilterSelect label="Student" value={studentPersonId} onValueChange={setStudentPersonId} allLabel="All students" options={students.map((item) => ({ value: item.person.id, label: personName(item.person) }))} />
              <FilterSelect label="Teacher or staff" value={staffPersonId} onValueChange={setStaffPersonId} allLabel="All staff" options={staff.map((item) => ({ value: item.person.id, label: personName(item.person) }))} />
              <FilterSelect label="Location" value={classroomId} onValueChange={setClassroomId} allLabel="All locations" options={classrooms.map((item) => ({ value: item.id, label: item.name }))} />
            </div>
            <Button variant="outline" size="icon" aria-label="Next schedule period" onClick={() => setDate(clampDate(shiftDate(date, periodMode === 'DAY' ? 1 : 7), schoolYearStart, schoolYearEnd))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          {loading ? <p className="py-8 text-center text-sm text-muted-foreground">Loading schedule…</p> : error ? <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">Unable to load the schedule: {error.message}</div> : (
            <ScheduleTimeGrid
              dates={dateRange(periodStart, periodEnd)}
              entries={visibleEntries}
              onAdd={openCreate}
              onAttendance={(blockId, attendanceDate) => navigate(`/app/${schoolSlug}/years/${yearSlug}/attendance/${blockId}?date=${attendanceDate}`)}
              onSelect={(blockId) => { const block = blocks.find((item) => item.id === blockId); if (block) openEdit(block); }}
              formatDate={formatPeriodDate}
            />
          )}
        </CardContent>
      </Card>

      <div className="schedule-print-surface schedule-print-only" aria-label="Printable schedule">
        {printPayload && <PrintProjection payload={printPayload} />}
      </div>

      <Card className="schedule-screen-controls" role="region" aria-labelledby="schedule-moment-heading">
        <CardHeader><CardTitle id="schedule-moment-heading">At a date and time</CardTitle><CardDescription>Computed occurrences at the selected moment. A recurring occurrence may be cancelled without changing the weekly pattern.</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2"><div className="space-y-2"><Label htmlFor="schedule-moment-date">Date</Label><Input id="schedule-moment-date" type="date" min={schoolYearStart} max={schoolYearEnd} value={date} onChange={(event) => setDate(clampDate(event.target.value, schoolYearStart, schoolYearEnd))} /></div><div className="space-y-2"><Label htmlFor="schedule-moment-time">Time</Label><Input id="schedule-moment-time" type="time" value={time} onChange={(event) => setTime(event.target.value)} /></div></div>
          {visibleMomentMessage && <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{visibleMomentMessage}</div>}
          {snapshotOccurrences.length === 0 ? <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">No schedule occurrences match this moment and these filters.</div> : <div className="grid gap-3 lg:grid-cols-2">{snapshotOccurrences.map(({ block, ...occurrence }) => <OccurrenceCard key={`${block.id}-${occurrence.date}`} block={block} date={String(occurrence.date)} timeLabel={occurrence.timeLabel} classroom={occurrence.classroom} state={occurrence.state} onEdit={() => openEdit(block)} onAttendance={block.attendanceEnabled ? () => navigate(`/app/${schoolSlug}/years/${yearSlug}/attendance/${block.id}?date=${String(occurrence.date)}`) : undefined} onCancel={!block.oneTime && occurrence.state !== 'ADDED' ? () => cancelOccurrence(block, String(occurrence.date)) : undefined} />)}</div>}
        </CardContent>
      </Card>

      <Card className="schedule-screen-controls">
        <CardHeader><div className="flex items-start justify-between gap-4"><div><CardTitle>All schedule blocks</CardTitle><CardDescription>Academic and operational blocks share the same scheduling model.</CardDescription></div><Button size="sm" variant="outline" onClick={() => openCreate()}><Plus className="mr-1 h-4 w-4" /> Add block</Button></div></CardHeader>
        <CardContent>{blocks.length === 0 ? <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">No schedule blocks have been added.</div> : <div className="divide-y rounded-md border">{blocks.map((block) => <div key={block.id} className="flex flex-col justify-between gap-3 p-4 sm:flex-row sm:items-center"><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><p className="font-medium">{block.displayTitle}</p><Badge variant="outline">{kindLabel(block.activityKind)}</Badge>{block.attendanceEnabled && <Badge variant="secondary">Attendance</Badge>}</div><p className="mt-1 text-sm text-muted-foreground">{blockPattern(block)} · {block.timeLabel}{block.classroom ? ` · ${block.classroom.name}` : ''}</p><ParticipantSummary block={block} /></div><div className="flex gap-1"><Button variant="ghost" size="icon" aria-label={`Edit ${block.displayTitle}`} onClick={() => openEdit(block)}><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="icon" aria-label={`Delete ${block.displayTitle}`} onClick={() => { setDeleteError(null); setDeleting(block); }}><Trash2 className="h-4 w-4 text-destructive" /></Button></div></div>)}</div>}</CardContent>
      </Card>

      {workspace && <ScheduleBlockDialog open={dialogOpen} onOpenChange={setDialogOpen} schoolYearId={workspace.id} schoolId={workspace.school.id} schoolYearStart={String(workspace.startDate)} schoolYearEnd={String(workspace.endDate)} defaultDate={dialogDate} defaultStartsAt={dialogStartsAt} defaultEndsAt={dialogEndsAt} classrooms={classrooms} staff={staff} students={students} classes={classes} courses={workspace.courses} terms={terms} gradeLevels={gradeLevels} block={editing} configureTermsHref={configureTermsHref} allowInlineClassCreation onComplete={async () => { if (activeSchoolYearId.current === workspace.id) await refetch(); }} onRefreshError={(message) => { if (activeSchoolYearId.current === workspace.id) setCopyMessage(message); }} onLocationCreated={async () => { if (activeSchoolYearId.current === workspace.id) await refetch(); }} onClassOptionsRefresh={async () => { if (activeSchoolYearId.current === workspace.id) await refetch(); }} />}

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete this schedule block?</AlertDialogTitle><AlertDialogDescription>{deleting?.displayTitle} and its dated overrides will be removed. Academic courses, classes, and rosters remain intact.</AlertDialogDescription></AlertDialogHeader>{deleteError && <div className="text-sm text-destructive">{deleteError}</div>}<AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={(event) => { event.preventDefault(); void handleDelete(); }}>Delete block</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
}

function FilterSelect({ label, value, onValueChange, allLabel, options }: { label: string; value: string; onValueChange: (value: string) => void; allLabel: string; options: ReadonlyArray<{ value: string; label: string }> }) {
  return <div className="space-y-2"><Label>{label}</Label><Select value={value} onValueChange={onValueChange}><SelectTrigger aria-label={label}><SelectValue /></SelectTrigger><SelectContent><SelectItem value={ALL}>{allLabel}</SelectItem>{options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select></div>;
}

function ParticipantSummary({ block }: { block: ScheduleBlockValue }) {
  if (block.audiences.length === 0 && block.staffAssignments.length === 0) return null;
  const labels = block.audiences.map((audience) => audience.classOffering?.displayName ?? audience.gradeLevelKey ?? (audience.kind === 'ALL_STUDENTS' ? 'All students' : audience.person ? personName(audience.person) : audience.kind));
  return <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Users className="h-3.5 w-3.5" />{labels.join(', ') || 'No student audience'}{block.staffAssignments.length > 0 ? ` · ${block.staffAssignments.length} staff` : ''}</p>;
}

function OccurrenceCard({ block, date, timeLabel, classroom, state, onEdit, onAttendance, onCancel }: { block: ScheduleBlockValue; date: string; timeLabel: string; classroom?: { id: string; name: string } | null; state?: string | null; onEdit: () => void; onAttendance?: () => void; onCancel?: () => void }) {
  return <div className="rounded-md border p-3"><div className="flex items-start justify-between gap-3"><div><p className="font-medium">{block.displayTitle}</p><p className="text-sm text-muted-foreground">{kindLabel(block.activityKind)}{state ? ` · ${kindLabel(state)}` : ''}</p></div><div className="flex gap-1">{onAttendance && <Button variant="outline" size="sm" onClick={onAttendance}><CalendarCheck className="mr-1 h-4 w-4" /> Attendance</Button>}<Button variant="ghost" size="icon" aria-label={`Edit ${block.displayTitle}`} onClick={onEdit}><Pencil className="h-4 w-4" /></Button>{onCancel && <Button variant="ghost" size="sm" onClick={onCancel}>Cancel date</Button>}</div></div><p className="mt-3 flex items-center gap-1.5 text-sm"><CalendarClock className="h-4 w-4 text-muted-foreground" /> {formatPeriodDate(date)} · {timeLabel}</p><p className="mt-1 flex items-center gap-1.5 text-sm"><MapPin className="h-4 w-4 text-muted-foreground" /> {classroom?.name ?? 'Location not assigned'}</p><ParticipantSummary block={block} /></div>;
}

function dateRange(startsOn: string, endsOn: string) {
  const dates: string[] = [];
  for (let current = startsOn; current <= endsOn; current = shiftDate(current, 1)) dates.push(current);
  return dates;
}

function periodSubtitle(startsOn: string, endsOn: string) {
  return `${formatPeriodDate(startsOn, true)}${endsOn === startsOn ? '' : ` – ${formatPeriodDate(endsOn, true)}`}`;
}

function PrintProjection({ payload }: { payload: PrintPayload }) {
  const dates = dateRange(payload.startsOn, payload.endsOn);
  const subtitle = periodSubtitle(payload.startsOn, payload.endsOn);

  if (payload.mode === 'STUDENT' && payload.studentPersonId && payload.studentName) {
    return (
      <PrintableAgenda
        title={`${payload.studentName}'s schedule`}
        subtitle={subtitle}
        entries={payload.entries.filter((entry) => entry.studentPeople.some((person) => person.id === payload.studentPersonId))}
        dates={dates}
      />
    );
  }
  if (payload.mode === 'TEACHER' && payload.teacherPersonId && payload.teacherName) {
    return (
      <PrintableAgenda
        title={`${payload.teacherName}'s schedule`}
        subtitle={subtitle}
        entries={payload.entries.filter((entry) => entry.staffPeople.some((person) => person.id === payload.teacherPersonId))}
        dates={dates}
      />
    );
  }
  if (payload.mode === 'ALL_STUDENTS') {
    return payload.students.map((student) => (
      <PrintableAgenda
        key={student.id}
        title={`${student.name}'s schedule`}
        subtitle={subtitle}
        entries={payload.entries.filter((entry) => entry.studentPeople.some((person) => person.id === student.id))}
        dates={dates}
        className="student-print-page"
      />
    ));
  }

  return (
    <PrintableAgenda
      title={`${payload.schoolName} school schedule`}
      subtitle={subtitle}
      entries={payload.entries}
      dates={dates}
    />
  );
}

function PrintableAgenda({ title, subtitle, entries, dates, className = '' }: { title: string; subtitle: string; entries: ReadonlyArray<SchedulePeriodEntry>; dates: ReadonlyArray<string>; className?: string }) {
  return <section className={`printable-agenda ${className}`}><header className="mb-4"><h2 className="break-words text-xl font-semibold">{title}</h2><p className="text-sm text-muted-foreground">{subtitle}</p></header><div className={`schedule-agenda-grid grid gap-3 ${dates.length === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-5'}`} style={{ '--schedule-day-count': Math.max(dates.length, 1) } as CSSProperties}>{dates.map((scheduleDate) => { const dayEntries = entries.filter((entry) => String(entry.date) === scheduleDate); return <section key={scheduleDate} className="min-w-0 rounded-md border p-3"><div className="border-b pb-2"><h3 className="break-words text-sm font-semibold">{formatPeriodDate(scheduleDate)}</h3></div>{dayEntries.length === 0 ? <p className="py-4 text-xs text-muted-foreground">No scheduled items</p> : <div className="divide-y">{dayEntries.map((entry) => <article key={`${entry.scheduleBlock.id}-${entry.date}-${entry.startsAt}`} className="break-inside-avoid py-2"><p className="text-xs font-medium">{entry.timeLabel}</p><p className="break-words text-sm font-semibold">{entry.title}</p>{entry.classOffering && entry.title !== entry.classOffering.displayName && <p className="break-words text-xs text-muted-foreground">{entry.classOffering.displayName}</p>}<p className="mt-1 break-words text-xs text-muted-foreground">{entry.classroom?.name ?? 'Location not assigned'}</p></article>)}</div>}</section>; })}</div></section>;
}
